import type { Middleware } from "openapi-fetch";

/**
 * Rolling log of the client's recent API calls, for feedback diagnostics:
 * the last MAX_API_CALL_ENTRIES requests with method, path, status and
 * duration. Lives here — at the app's single HTTP seam (the openapi-fetch
 * client every hook goes through) — rather than monkey-patching global
 * fetch. Observe-only: recording never changes a request or response, and
 * a recording failure never fails a call.
 */

export interface ApiCallLogEntry {
  method: string;
  /** Path + search only (the client always talks to the app's own API). */
  url: string;
  /** HTTP status; null when the request never got a response. */
  status: number | null;
  duration_ms: number;
  timestamp: string;
  /**
   * The error envelope's log-correlation trace id, when the response was an
   * error that carried one. Success responses expose no correlation id
   * today (no request-id header exists — see the feedback PR's follow-ups).
   */
  trace_id?: string;
}

/** Rolling-buffer size — enough recent traffic to reconstruct a report. */
export const MAX_API_CALL_ENTRIES = 15;

const entries: ApiCallLogEntry[] = [];

/** Append one call, evicting the oldest beyond MAX_API_CALL_ENTRIES. */
export function recordApiCall(entry: ApiCallLogEntry): void {
  entries.push(entry);
  if (entries.length > MAX_API_CALL_ENTRIES) {
    entries.splice(0, entries.length - MAX_API_CALL_ENTRIES);
  }
}

/** The recent calls, oldest first (a copy — safe to serialize/mutate). */
export function snapshotApiCalls(): ApiCallLogEntry[] {
  return entries.map((entry) => ({ ...entry }));
}

/** Test hook: reset the module-level buffer. */
export function clearApiCallLog(): void {
  entries.length = 0;
}

/** Path + search of a request URL; the raw string when parsing fails. */
function pathAndSearch(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

/**
 * The middleware that feeds the log. Register it AFTER the auth middleware:
 * openapi-fetch runs onResponse in reverse registration order, so this one
 * observes the response (and stamps the entry) before the auth middleware
 * turns an error status into a throw.
 */
export function createApiCallLogMiddleware(): Middleware {
  // Start times keyed by openapi-fetch's per-request id; entries are
  // deleted on settle, so an abandoned request leaks one number at most
  // until its id is reused — acceptable for a diagnostics buffer.
  const startedAt = new Map<string, number>();

  return {
    onRequest({ id }) {
      startedAt.set(id, performance.now());
    },
    async onResponse({ id, request, response }) {
      const started = startedAt.get(id);
      startedAt.delete(id);
      let traceId: string | undefined;
      if (!response.ok) {
        // Error envelopes carry `error.trace_id` for log correlation —
        // exactly the calls a feedback report most wants to pin down.
        try {
          const body = await response.clone().json();
          if (typeof body?.error?.trace_id === "string") {
            traceId = body.error.trace_id;
          }
        } catch {
          // Non-JSON error body — nothing to correlate.
        }
      }
      recordApiCall({
        method: request.method,
        url: pathAndSearch(request.url),
        status: response.status,
        duration_ms: Math.round(
          performance.now() - (started ?? performance.now())
        ),
        timestamp: new Date().toISOString(),
        ...(traceId ? { trace_id: traceId } : {})
      });
    },
    onError({ id, request }) {
      // Network-level failure: no response ever arrived.
      const started = startedAt.get(id);
      startedAt.delete(id);
      recordApiCall({
        method: request.method,
        url: pathAndSearch(request.url),
        status: null,
        duration_ms: Math.round(
          performance.now() - (started ?? performance.now())
        ),
        timestamp: new Date().toISOString()
      });
    }
  };
}
